import {Visibility, VisibilityOff} from "@mui/icons-material";
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
} from "@mui/material";
import {MIN_PASSWORD_LENGTH} from "crqlar-react-common/dist/models/identity/user.model";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {useSnackbar} from "notistack";
import {FormEvent, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

interface Props {
    open: boolean;
    onClose: () => void;
}

const ChangeMyPasswordDialog = ({open, onClose}: Props) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string>();

    useEffect(() => {
        if (!open) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswords(false);
            setPasswordError(undefined);
        }
    }, [open]);

    const newPasswordTooShort = !!newPassword && newPassword.trim().length < MIN_PASSWORD_LENGTH;
    const confirmMismatch = !!confirmPassword && confirmPassword !== newPassword;
    const canSubmitPassword =
        !!currentPassword.trim() &&
        !!newPassword.trim() &&
        !newPasswordTooShort &&
        confirmPassword === newPassword;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!canSubmitPassword || changingPassword) return;

        setChangingPassword(true);
        setPasswordError(undefined);

        try {
            await MyAccountService.changePassword(currentPassword, newPassword);
            enqueueSnackbar(t('Password changed'), {variant: 'success'});
            onClose();
        } catch (e: any) {
            const status = e?.response?.status;
            setPasswordError(
                status === 401
                    ? t('Current password is not correct')
                    : status === 400
                        ? t('Choose a different password with at least {{count}} characters', {count: MIN_PASSWORD_LENGTH})
                        : t('Could not change the password. Please try again')
            );
        } finally {
            setChangingPassword(false);
        }
    };

    const passwordVisibilityAdornment = (
        <InputAdornment position={'end'}>
            <IconButton
                onClick={() => setShowPasswords(value => !value)}
                edge={'end'}
                size={'small'}
                disabled={changingPassword}
                aria-label={showPasswords ? t('Hide password') : t('Show password')}
            >
                {showPasswords ? <VisibilityOff fontSize={'small'}/> : <Visibility fontSize={'small'}/>}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Dialog
            open={open}
            onClose={() => !changingPassword && onClose()}
            maxWidth={'xs'}
            fullWidth
            PaperProps={{component: 'form', onSubmit: handleSubmit}}
        >
            <DialogTitle>
                {t('Change password')}
            </DialogTitle>

            <DialogContent>
                <Stack gap={2} sx={{mt: 0.5}}>
                    <Alert severity={'info'} variant={'outlined'}>
                        {t('Enter your current password, then choose a new one.')}
                    </Alert>

                    {passwordError && (
                        <Alert severity={'error'} variant={'outlined'}>
                            {passwordError}
                        </Alert>
                    )}

                    <TextField
                        id={'current-password'}
                        autoFocus
                        size={'small'}
                        fullWidth
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete={'current-password'}
                        label={t('Current password')}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={changingPassword}
                        InputProps={{endAdornment: passwordVisibilityAdornment}}
                    />

                    <TextField
                        id={'new-password'}
                        size={'small'}
                        fullWidth
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete={'new-password'}
                        label={t('New password')}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={changingPassword}
                        error={newPasswordTooShort}
                        helperText={newPasswordTooShort
                            ? t('Password must be at least {{count}} characters long', {count: MIN_PASSWORD_LENGTH})
                            : ''}
                        InputProps={{endAdornment: passwordVisibilityAdornment}}
                    />

                    <TextField
                        id={'confirm-new-password'}
                        size={'small'}
                        fullWidth
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete={'new-password'}
                        label={t('Confirm password')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={changingPassword}
                        error={confirmMismatch}
                        helperText={confirmMismatch ? t('Passwords do not match') : ''}
                        InputProps={{endAdornment: passwordVisibilityAdornment}}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={changingPassword}>
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    type={'submit'}
                    variant={'contained'}
                    loading={changingPassword}
                    disabled={!canSubmitPassword}
                >
                    {t('Change password')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeMyPasswordDialog;
